import React, { useState, useRef, useEffect } from "react"
import { Collapse } from "@mantine/core"
import { IconAdjustmentsHorizontal } from "@tabler/icons-react"
import { FilterContent } from "QuorumGrassroots/widgets/ReusableComponents/FilterButton/FilterContent"
import {
    FilterButtonContainer,
    StyledFilterButton,
    StyledPaper,
} from "QuorumGrassroots/widgets/ReusableComponents/FilterButton/FilterButton.styles"
import {
    FilterState,
    initializeFilters,
    DEFAULT_TYPE_OPTIONS,
    DEFAULT_PARTY_OPTIONS,
} from "QuorumGrassroots/widgets/ReusableComponents/FilterButton/FilterButton.helper"

export interface FilterButtonProps {
    showTypeFilter?: boolean
    onFiltersChange?: (filters: FilterState) => void
}

export const FilterButton: React.FC<FilterButtonProps> = ({ showTypeFilter = false, onFiltersChange }) => {
    const [opened, setOpened] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const [filters, setFilters] = useState<FilterState>(() => initializeFilters())

    const handleFiltersChange = (newFilters: FilterState) => {
        setFilters(newFilters)
        onFiltersChange?.(newFilters)
    }

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpened(false)
            }
        }

        if (opened) {
            document.addEventListener("mousedown", handleClickOutside)
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [opened])

    // Handle keyboard navigation (Escape to close)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && opened) {
                setOpened(false)
                buttonRef.current?.focus()
            }
        }

        if (opened) {
            document.addEventListener("keydown", handleKeyDown)
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown)
        }
    }, [opened])

    return (
        <FilterButtonContainer ref={containerRef}>
            <StyledFilterButton
                ref={buttonRef}
                variant="subtle"
                onClick={() => setOpened((o) => !o)}
                aria-label="Filter options"
                aria-expanded={opened}
                aria-haspopup="dialog"
                data-expanded={opened ? true : undefined}
            >
                <IconAdjustmentsHorizontal size={20} aria-hidden="true" />
            </StyledFilterButton>

            <Collapse in={opened}>
                <StyledPaper shadow="xl" radius="12px" role="dialog" aria-labelledby="filter-menu-title">
                    <FilterContent
                        filters={filters}
                        showTypeFilter={showTypeFilter}
                        typeOptions={DEFAULT_TYPE_OPTIONS}
                        partyOptions={DEFAULT_PARTY_OPTIONS}
                        onFiltersChange={handleFiltersChange}
                    />
                </StyledPaper>
            </Collapse>
        </FilterButtonContainer>
    )
}
